import { useState, FormEvent } from 'react';

const FreeTrial = () => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('fullName', (form.elements.namedItem('fullName') as HTMLInputElement).value);
    data.append('phone', (form.elements.namedItem('phone') as HTMLInputElement).value);
    data.append('city', (form.elements.namedItem('city') as HTMLInputElement).value);

    try {
      await fetch('https://readdy.ai/api/form/d77uuebll6jbq2vdd1b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="test" className="py-12 md:py-20 bg-gradient-to-br from-purple-950/20 to-black">
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16 px-4">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">Free 24h Trial</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Test our service for 24 hours totally free. No commitment, no credit card required.
            </p>
          </div>

          {/* Form */}
          <div className="flex justify-center mb-12">
            <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-md w-full p-6">
              <h3 className="font-semibold text-white text-2xl text-center mb-6">Direct WhatsApp Contact</h3>

              {status === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-emerald-400 text-3xl"></i>
                  </div>
                  <p className="text-emerald-400 font-semibold text-lg mb-2">Request Sent!</p>
                  <p className="text-gray-400 text-sm">We will contact you via WhatsApp shortly.</p>
                </div>
              ) : (
                <form data-readdy-form="true" className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">Full name</label>
                    <input
                      className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm"
                      placeholder="Your full name"
                      maxLength={100}
                      required
                      name="fullName"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">Phone number</label>
                    <input
                      className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm"
                      placeholder="Your phone number"
                      maxLength={13}
                      required
                      type="tel"
                      name="phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1">City</label>
                    <input
                      className="w-full h-10 rounded-md border border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 px-3 py-2 text-sm"
                      placeholder="Your city"
                      maxLength={100}
                      required
                      name="city"
                    />
                  </div>
                  {status === 'error' && (
                    <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full h-10 rounded-md bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === 'loading' ? 'Sending...' : 'Get the Trial via WhatsApp'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 px-4">
            {[
              { icon: 'ri-time-line', title: '24 Full Hours', desc: 'Unlimited access for 24 hours to all our services' },
              { icon: 'ri-user-unfollow-line', title: 'No Commitment', desc: 'No credit card required, automatic cancellation' },
              { icon: 'ri-flashlight-line', title: 'Instant Activation', desc: 'Receive your login details by email in less than 5 minutes' },
            ].map((item) => (
              <div key={item.title} className="flex items-start space-x-4">
                <div className="bg-gradient-to-r from-orange-500/20 via-pink-500/20 to-purple-500/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-pink-400 text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* What you can test */}
          <div className="mt-12 p-6 bg-gray-800/50 rounded-lg border border-gray-700">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">What you can test:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="text-gray-300">
                <strong className="text-pink-500 block mb-1">Live Channels</strong>
                Sports, news, entertainment &amp; more
              </div>
              <div className="text-gray-300">
                <strong className="text-pink-500 block mb-1">Full Library</strong>
                Extensive movies and series catalog
              </div>
              <div className="text-gray-300">
                <strong className="text-pink-500 block mb-1">Premium Quality</strong>
                4K, Full HD quality
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FreeTrial;
